import { Component, EventEmitter, Input, OnInit, Output, inject, signal, computed, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FatService, FatDetailDto, FatFieldDto, FatCategoryDto, FatSubcategoryDto, FatSignatureDto } from '../../../../core/services/fat.service';
import { FatPhotosDialogComponent } from './fat-photos-dialog/fat-photos-dialog.component';
import { FatDocumentsDialogComponent } from './fat-documents-dialog/fat-documents-dialog.component';
import { Project, SousTraitant, StepPermissions, Workflow, WorkflowStep } from '../../../../core/models/models';
import { WorkflowService } from '../../../../core/services/workflow.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog.component';
import { ProjectDocumentsComponent } from '../document/project-documents.component';
import { ValidationErrorsDialogComponent } from './validation-errors-dialog/validation-errors-dialog.component';
import { TaskNotificationService } from '../../../../core/services/task-notification.service';
import { ProjectService } from '../../../../core/services/project.service';
import { SousTraitantService } from '../../../../core/services/sous-traitant.service';

@Component({
  selector: 'app-fat-fill',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatExpansionModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatTabsModule,
    ConfirmDialogComponent,
    ProjectDocumentsComponent,
    ValidationErrorsDialogComponent
  ],
  templateUrl: './fat-fill.component.html',
  styleUrl: './fat-fill.component.scss'
})
export class FatFillComponent implements OnInit, AfterViewInit, OnDestroy {
  private fatService = inject(FatService);
  private workflowService = inject(WorkflowService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private taskNotificationService = inject(TaskNotificationService);
  private projectService = inject(ProjectService);
  private sousTraitantService = inject(SousTraitantService);

  @Input({ required: true }) projectId!: number;
  @Input({ required: true }) fatId!: number;
  @Output() back = new EventEmitter<void>();
  @Output() error = new EventEmitter<string>();

  @ViewChild('signatureCanvas1') signatureCanvas1?: ElementRef<HTMLCanvasElement>;
  @ViewChild('signatureCanvas2') signatureCanvas2?: ElementRef<HTMLCanvasElement>;

  private signaturePad1: any;
  private signaturePad2: any;
  private resizeObserver?: ResizeObserver;

  showConfirmValidate = signal(false);
  showValidationErrors = signal(false);
  isSidebarCollapsed = signal(false);
  validationErrorTitle = signal('Éléments non remplis');
  validationErrorMessage = signal('Veuillez renseigner tous les éléments, les commentaires et les pièces jointes obligatoires de la FAT pour les catégories suivantes :');
  missingCategories = signal<string[]>([]);

  fat = signal<FatDetailDto | null>(null);
  activeCategoryIndex = signal(0);
  isLoading = signal(false);
  isLoadingExport = signal(false);
  savingFields = signal(new Set<number>());
  savingCategories = signal(new Set<number>());
  workflowPermissions = signal<StepPermissions | null>(null);
  currentWorkflow = signal<Workflow | null>(null);
  availableSubcontractors = signal<SousTraitant[]>([]);
  categorySubcontractorQueries = signal<Record<number, string>>({});

  canSign = computed(() => {
    const role = this.authService.currentUser()?.role;
    return ['ROLE_ADMIN', 'ROLE_RESP_AFFAIRE', 'ROLE_CONTROLEUR'].includes(role || '');
  });

  isAnyFieldNonConforme = computed(() => {
    const f = this.fat();
    if (!f) return false;
    return f.categories.some(cat =>
      cat.subcategories.some(sub =>
        sub.fields.some(field => field.result === 'Non Conforme')
      )
    );
  });


  activeCategory = computed(() => {
    const f = this.fat();
    if (!f) return null;
    return f.categories[this.activeCategoryIndex()] || null;
  });

  getCategoryStatus(category: FatCategoryDto): 'conforme' | 'non-conforme' | 'incomplete' | 'default' {
    const allFields = category.subcategories.flatMap((sub: FatSubcategoryDto) => sub.fields);

    if (allFields.length === 0) return 'default';

    if (this.isCategorySubcontractorMissing(category)) {
      return 'incomplete';
    }

    // Incomplète si des commentaires obligatoires manquent ou si des photos obligatoires manquent
    const hasMissingComments = allFields.some((field: FatFieldDto) => {
      const hasResult = field.result !== null && field.result !== undefined && field.result !== '';
      const hasRequiredComment = !this.isCommentMandatory(field) || (field.comment !== null && field.comment !== undefined && field.comment.trim() !== '');
      const hasRequiredAttachments = !this.hasMinimumAttachments(field);
      return !hasResult || !hasRequiredComment || hasRequiredAttachments;
    });

    if (hasMissingComments && category.name !== 'Généralités') {
      return 'incomplete';
    }

    const results = allFields.map((f: FatFieldDto) => f.result);

    // Rouge si au moins un des résultats est Non Conforme
    if (results.some((r: string | null) => r === 'Non Conforme')) {
      return 'non-conforme';
    }

    // Vert si la catégorie est remplie et que ses champs ont tous un résultat conforme (ou NA)
    const allFilled = results.every((r: string | null) => r !== null && r !== undefined && r !== '');
    const allConforme = results.every((r: string | null) => r === 'Conforme' || r === 'Non Applicable');

    if (allFilled && allConforme) {
      return 'conforme';
    }

    return 'default';
  }

  getSubcategoryStatus(subcategory: FatSubcategoryDto): 'conforme' | 'non-conforme' | 'incomplete' | 'default' {
    const fields = subcategory.fields;
    console.log('fields', fields);
    if (!fields || fields.length === 0) return 'default';

    // Incomplète si des commentaires obligatoires manquent ou si des photos obligatoires manquent
    const hasMissingComments = fields.some((field: FatFieldDto) => {
      const hasResult = field.result !== null && field.result !== undefined && field.result !== '';
      const hasRequiredComment = !this.isCommentMandatory(field) || (field.comment !== null && field.comment !== undefined && field.comment.trim() !== '');
      const hasRequiredAttachments = !this.hasMinimumAttachments(field);
      console.log('hasRequiredAttachments', hasRequiredAttachments);
      console.log('hasRequiredComment', hasRequiredComment);
      console.log('hasResult', hasResult);
      console.log('attachment', this.hasMinimumAttachments(field));
      return !hasResult || !hasRequiredComment || hasRequiredAttachments;
    });

    if (hasMissingComments) {
      console.log('hasMissingComments', hasMissingComments);
      return 'incomplete';
    }

    const results = fields.map((f: FatFieldDto) => f.result);

    // Rouge si au moins un des résultats est Non Conforme
    if (results.some((r: string | null) => r === 'Non Conforme')) {
      return 'non-conforme';
    }

    // Vert si tous les champs sont remplis et conformes (ou NA)
    const allFilled = results.every((r: string | null) => r !== null && r !== undefined && r !== '');
    const allConforme = results.every((r: string | null) => r === 'Conforme' || r === 'Non Applicable');

    if (allFilled && allConforme) {
      return 'conforme';
    }

    return 'default';
  }

  ngOnInit(): void {
    this.loadProjectSubcontractors();
    this.loadFat();
  }

  ngAfterViewInit(): void {
    this.initSignaturePads();

    // Observer les changements de taille pour ajuster le canvas
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
    });

    const container = document.querySelector('.fat-fill-container');
    if (container) {
      this.resizeObserver.observe(container);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private async initSignaturePads(): Promise<void> {
    // Dynamically import signature_pad to avoid issues if not present or during SSR
    try {
      const SignaturePad = (await import('signature_pad')).default;

      // Initialiser le pad 1 seulement s'il n'existe pas déjà
      if (this.signatureCanvas1?.nativeElement && !this.signaturePad1) {
        this.signaturePad1 = new SignaturePad(this.signatureCanvas1.nativeElement, {
          backgroundColor: 'rgb(248, 250, 252)',
          penColor: 'rgb(30, 41, 59)'
        });
      }

      // Initialiser le pad 2 seulement s'il n'existe pas déjà
      if (this.signatureCanvas2?.nativeElement && !this.signaturePad2) {
        this.signaturePad2 = new SignaturePad(this.signatureCanvas2.nativeElement, {
          backgroundColor: 'rgb(248, 250, 252)',
          penColor: 'rgb(30, 41, 59)'
        });
      }

      this.resizeCanvas();
    } catch (e) {
      console.error('Could not load signature_pad', e);
    }
  }

  private resizeCanvas(): void {
    [
      { pad: this.signaturePad1, canvas: this.signatureCanvas1 },
      { pad: this.signaturePad2, canvas: this.signatureCanvas2 }
    ].forEach(item => {
      if (item.pad && item.canvas?.nativeElement) {
        const canvas = item.canvas.nativeElement;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);

        // On ne redimensionne que si la taille affichée a changé
        const rect = canvas.getBoundingClientRect();

        // Ne pas redimensionner si le canvas n'est pas visible (width ou height = 0)
        if (rect.width === 0 || rect.height === 0) {
          return;
        }

        if (canvas.width !== rect.width * ratio || canvas.height !== rect.height * ratio) {
          canvas.width = rect.width * ratio;
          canvas.height = rect.height * ratio;
          canvas.getContext('2d')?.scale(ratio, ratio);
          item.pad.clear(); // Malheureusement clear() est nécessaire après redimensionnement
        }
      }
    });
  }

  clearSignature(controllerNumber: number): void {
    if (controllerNumber === 1 && this.signaturePad1) {
      this.signaturePad1.clear();
    } else if (controllerNumber === 2 && this.signaturePad2) {
      this.signaturePad2.clear();
    }
  }

  loadFat(): void {
    this.isLoading.set(true);
    this.fatService.getFatDetail(this.projectId, this.fatId).subscribe({
      next: (data) => {
        this.fat.set(data);
        this.loadWorkflowPermissions();
        this.loadWorkflowDetails();
        this.isLoading.set(false);
      },
      error: () => {
        this.error.emit("Erreur lors du chargement du détail de la FAT");
        this.isLoading.set(false);
      }
    });
  }

  private loadProjectSubcontractors(): void {
    this.projectService.getProjectById(this.projectId).pipe(
      switchMap((project: Project) => {
        const linkedIds = new Set(project.sousTraitantIds || []);
        if (linkedIds.size === 0) {
          return of([] as SousTraitant[]);
        }
        return this.sousTraitantService.getAllSousTraitants().pipe(
          switchMap((subcontractors: SousTraitant[]) => of(
            subcontractors.filter(st => !!st.id && linkedIds.has(st.id))
          ))
        );
      })
    ).subscribe({
      next: (subcontractors) => this.availableSubcontractors.set(subcontractors),
      error: () => this.availableSubcontractors.set([])
    });
  }

  confirmValidate(): void {
    const missing = this.getMissingCategories();
    if (missing.length > 0) {
      this.missingCategories.set(missing);
      this.showValidationErrors.set(true);
      return;
    }
    this.showConfirmValidate.set(true);
  }

  cancelValidate(): void {
    this.showConfirmValidate.set(false);
  }

  closeValidationErrors(): void {
    this.showValidationErrors.set(false);
    this.missingCategories.set([]);
    this.validationErrorTitle.set('Éléments non remplis');
    this.validationErrorMessage.set('Veuillez renseigner tous les éléments, les commentaires et les photos obligatoires de la FAT pour les catégories suivantes :');
  }

  getMissingCategories(): string[] {
    const f = this.fat();
    if (!f) return [];

    const missing: string[] = [];
    f.categories.forEach((cat: FatCategoryDto) => {
      // Pour "Généralités", on considère qu'elle est toujours "remplie" ou qu'elle n'a pas de champs FAT
      if (cat.name === 'Généralités') return;

      if (this.isCategorySubcontractorMissing(cat)) {
        missing.push(cat.name);
        return;
      }

      const allFields = cat.subcategories.flatMap((sub: FatSubcategoryDto) => sub.fields);
      const isComplete = allFields.every((field: FatFieldDto) => {
        const hasResult = field.result !== null && field.result !== undefined && field.result !== '';
        const hasRequiredComment = !this.isCommentMandatory(field) || (field.comment !== null && field.comment !== undefined && field.comment.trim() !== '');
        const hasRequiredAttachments = this.hasMinimumAttachments(field);
        return hasResult && hasRequiredComment && hasRequiredAttachments;
      });

      if (!isComplete) {
        missing.push(cat.name);
      }
    });

    if (!this.hasAllSignatures()) {
      missing.push('Signatures');
    }

    return missing;
  }

  validateWorkflow(): void {
    this.showConfirmValidate.set(false);
    if (!this.canValidateWorkflow()) return;

    const missing = this.getMissingCategories();
    if (missing.length > 0) {
      this.missingCategories.set(missing);
      this.showValidationErrors.set(true);
      return;
    }

    this.fatService.validateWorkflow(this.projectId, this.fatId).subscribe({
      next: (data) => {
        this.fat.set(data);
        this.loadWorkflowPermissions();
        this.loadWorkflowDetails();
      },
      error: (err) => this.error.emit(err?.error?.message || 'Validation workflow FAT impossible')
    });
  }


  toggleSidebar(): void {
    this.isSidebarCollapsed.update(v => !v);
  }

  selectCategory(index: number): void {
    this.activeCategoryIndex.set(index);
    if (this.fat()) {
      const isSignatureTab = index === this.fat()!.categories.length;
      if (isSignatureTab) {
        // Laisser un peu de temps au DOM de s'afficher
        setTimeout(() => this.initSignaturePads(), 100);
      }
    }
  }

  onSignatureTabChange(): void {
    // Quand on change d'onglet dans les signatures, réinitialiser les pads
    // pour s'assurer que le canvas est bien initialisé
    setTimeout(() => this.initSignaturePads(), 100);
  }

  updateField(field: FatFieldDto): void {
    if (!this.isFatEditable()) {
      return;
    }
    if (this.savingFields().has(field.id)) return;

    this.savingFields.update(set => new Set(set).add(field.id));
    this.fatService.updateField(this.projectId, field.id, {
      result: field.result,
      comment: field.comment
    }).subscribe({
      next: () => {
        this.savingFields.update(set => {
          const newSet = new Set(set);
          newSet.delete(field.id);
          return newSet;
        });
        // On force la mise à jour du signal pour recalculer les computed (notamment isAnyFieldNonConforme)
        this.fat.update(current => current ? { ...current } : null);
      },
      error: () => {
        this.error.emit(`Erreur lors de la sauvegarde du champ ${field.name}`);
        this.savingFields.update(set => {
          const newSet = new Set(set);
          newSet.delete(field.id);
          return newSet;
        });
      }
    });
  }

  updateCategorySubcontractor(category: FatCategoryDto, subcontractorId: number | null): void {
    if (!this.isFatEditable() || this.savingCategories().has(category.id)) {
      return;
    }

    this.savingCategories.update(set => new Set(set).add(category.id));
    this.fatService.updateCategory(this.projectId, category.id, { subcontractorId }).subscribe({
      next: (updatedCategory) => {
        category.subcontractorId = updatedCategory.subcontractorId ?? null;
        category.subcontractorName = updatedCategory.subcontractorName ?? null;
        this.setCategorySubcontractorQuery(category.id, updatedCategory.subcontractorName || '');
        this.savingCategories.update(set => {
          const newSet = new Set(set);
          newSet.delete(category.id);
          return newSet;
        });
        this.fat.update(current => current ? { ...current } : null);
      },
      error: () => {
        this.resetCategorySubcontractorQuery(category);
        this.error.emit(`Erreur lors de la sauvegarde du sous-traitant pour ${category.name}`);
        this.savingCategories.update(set => {
          const newSet = new Set(set);
          newSet.delete(category.id);
          return newSet;
        });
      }
    });
  }

  onCategorySubcontractorQueryChange(category: FatCategoryDto, value: string | SousTraitant | null): void {
    if (value && typeof value === 'object') {
      this.setCategorySubcontractorQuery(category.id, value.denomination);
      return;
    }
    this.setCategorySubcontractorQuery(category.id, value || '');
  }

  onCategorySubcontractorSelected(category: FatCategoryDto, subcontractor: SousTraitant): void {
    this.setCategorySubcontractorQuery(category.id, subcontractor.denomination);
    this.updateCategorySubcontractor(category, subcontractor.id ?? null);
  }

  getFilteredSubcontractors(category: FatCategoryDto): SousTraitant[] {
    const query = this.getCategorySubcontractorQuery(category).trim().toLowerCase();
    const subcontractors = this.availableSubcontractors();
    if (!query) {
      return subcontractors;
    }

    return subcontractors.filter(st =>
      st.denomination.toLowerCase().includes(query)
      || st.codeComptable.toLowerCase().includes(query)
      || st.ville.toLowerCase().includes(query)
    );
  }

  getCategorySubcontractorQuery(category: FatCategoryDto): string {
    return this.categorySubcontractorQueries()[category.id] ?? category.subcontractorName ?? '';
  }

  resetCategorySubcontractorQuery(category: FatCategoryDto): void {
    this.setCategorySubcontractorQuery(category.id, category.subcontractorName || '');
  }

  displaySubcontractor(subcontractor: SousTraitant | string | null): string {
    if (!subcontractor) {
      return '';
    }
    return typeof subcontractor === 'string' ? subcontractor : subcontractor.denomination;
  }

  isCategorySubcontractorMissing(category: FatCategoryDto): boolean {
    return category.name !== 'Généralités' && !category.subcontractorId;
  }

  private setCategorySubcontractorQuery(categoryId: number, value: string): void {
    this.categorySubcontractorQueries.update(current => ({
      ...current,
      [categoryId]: value
    }));
  }

  onCategorySubcontractorBlur(category: FatCategoryDto): void {
    // Si l'input est vide et qu'un sous-traitant était défini, on le supprime (subcontractorId = null)
    const currentQuery = this.getCategorySubcontractorQuery(category).trim();
    if (currentQuery === '') {
      if (category.subcontractorId) {
        this.updateCategorySubcontractor(category, null);
        return;
      }
      // Rien de défini, on garde vide
      return;
    }
    // Sinon on rétablit l'affichage sur le nom actuel s'il existe
    this.resetCategorySubcontractorQuery(category);
  }

  clearCategorySubcontractor(category: FatCategoryDto): void {
    if (!this.isFatEditable()) return;
    this.setCategorySubcontractorQuery(category.id, '');
    this.updateCategorySubcontractor(category, null);
  }

  onFilesSelected(field: FatFieldDto, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const files = Array.from(input.files);
    const taskId = files.length > 1
      ? this.taskNotificationService.start('Photos FAT', `Upload de ${files.length} images en cours...`)
      : this.taskNotificationService.start('Photos FAT', 'Upload de l\'image en cours...');
    let completed = 0;

    files.forEach(file => {
      this.fatService.uploadPhotoAsync(this.projectId, field.id, file).pipe(
        switchMap(job => this.fatService.waitForPhotoUpload(this.projectId, job.jobId))
      ).subscribe({
        next: (photo) => {
          if (!field.photos) field.photos = [] as any;
          field.photos!.push(photo);
          completed += 1;
          if (completed === files.length) {
            this.taskNotificationService.succeed(taskId, files.length > 1 ? `${files.length} images uploadées.` : 'Image uploadée.');
          }
        },
        error: () => {
          this.taskNotificationService.fail(taskId, 'L\'upload des images a échoué.');
          this.error.emit(`Erreur lors de l'upload de la photo pour ${field.name}`);
        }
      });
    });

    // reset value to allow re-selecting the same file
    input.value = '';
  }

  openPhotosDialog(field: FatFieldDto): void {
    const dialogRef = this.dialog.open(FatPhotosDialogComponent, {
      data: {
        projectId: this.projectId,
        field: field,
        readonly: !this.isFatEditable()
      },
      width: '800px',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((updatedField: FatFieldDto | undefined) => {
      if (updatedField) {
        // Mettre à jour les photos du champ avec les données retournées par le dialog
        field.photos = updatedField.photos;
      }
      // Forcer la mise à jour du signal fat pour déclencher le change detection
      // même si le dialog a été fermé sans résultat (Escape, clic en dehors)
      this.fat.update(currentFat => currentFat ? { ...currentFat } : null);
    });
  }

  openDocumentsDialog(field: FatFieldDto): void {
    const dialogRef = this.dialog.open(FatDocumentsDialogComponent, {
      data: {
        projectId: this.projectId,
        field: field,
        readonly: !this.isFatEditable()
      },
      width: '700px',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((updatedField: FatFieldDto | undefined) => {
      if (updatedField) {
        field.documents = updatedField.documents;
      }
      this.fat.update(currentFat => currentFat ? { ...currentFat } : null);
    });
  }

  getResults(possibleResults?: string): string[] {
    if (!possibleResults) return ['Conforme', 'Non Conforme', 'Non Applicable'];
    return possibleResults.split(',').map((r: string) => r.trim());
  }

  isCommentMandatory(field: FatFieldDto): boolean {
    if (field.result === 'Non Applicable') return false;
    if (field.commentMandatory) return true;
    return field.result === 'Non Conforme';

  }

  hasMinimumAttachments(field: FatFieldDto): boolean {
    if (field.result === 'Non Applicable') return true;
    const minRequired = field.minPhotosNbr || 0;
    if (field.type === 'DOCUMENT') {
      const currentCount = field.documents?.length || 0;
      return currentCount >= minRequired;
    }
    if (field.type === 'ACTION') { return true;}

    const currentCount = field.photos?.length || 0;
    return currentCount >= minRequired;
  }

  isFieldNonConforme(field: FatFieldDto): boolean {
    return field.result === 'Non Conforme';
  }

  isFieldIncomplete(field: FatFieldDto): boolean {
    const hasResult = field.result !== null && field.result !== undefined && field.result !== '';
    const hasRequiredComment = !this.isCommentMandatory(field) || (field.comment !== null && field.comment !== undefined && field.comment.trim() !== '');
    const hasRequiredAttachments = this.hasMinimumAttachments(field);
    return !hasResult || !hasRequiredComment || !hasRequiredAttachments;
  }

  isFatEditable(): boolean {
    const f = this.fat();
    if (!f) return false;
    if (f.statusMeta) return f.statusMeta === 'DRAFT';
    return f.status === 'EN_COURS';
  }

  isWorkflowCompleted(): boolean {
    const f = this.fat();
    if (!f) return false;

    // Si le statut est "FAT conforme" ou "FAT non conforme", c'est terminé.
    if (f.status === 'FAT conforme' || f.status === 'FAT non conforme') {
      return true;
    }

    const workflow = this.currentWorkflow();
    if (!workflow?.steps?.length) {
      return false;
    }
    const lastStep = workflow.steps.reduce((acc: WorkflowStep, step: WorkflowStep) => step.stepOrder > acc.stepOrder ? step : acc, workflow.steps[0]);
    if (f.statusId && lastStep.finalStatusId) {
      return f.statusId === lastStep.finalStatusId;
    }
    if (f.status && lastStep.finalStatusName) {
      return f.status === lastStep.finalStatusName;
    }
    return false;
  }

  private loadWorkflowPermissions(): void {
    this.fatService.getWorkflowPermissions(this.projectId, this.fatId).subscribe({
      next: (permissions) => this.workflowPermissions.set(permissions),
      error: () => this.workflowPermissions.set(null)
    });
  }

  private loadWorkflowDetails(): void {
    const f = this.fat();
    if (!f?.workflowId) {
      this.currentWorkflow.set(null);
      return;
    }
    this.workflowService.getWorkflowById(f.workflowId).subscribe({
      next: (workflow) => this.currentWorkflow.set(workflow),
      error: () => this.currentWorkflow.set(null)
    });
  }

  canValidateWorkflow(): boolean {
    return this.workflowPermissions()?.canValidate ?? false;
  }

  isFatConforme(): boolean {
    return this.fat()?.status === 'FAT conforme';
  }

  isFatTerminee(): boolean {
    return this.fat()?.status === 'FAT terminée';
  }

  isFatNonConforme(): boolean {
    return this.fat()?.status === 'FAT non conforme';
  }

  hasAllSignatures(): boolean {
    const f = this.fat();
    if (!f) return false;
    const signaturesCount = f.signatures?.length || 0;

    if (this.isAnyFieldNonConforme()) {
      return signaturesCount >= 1;
    } else {
      return signaturesCount >= 2;
    }
  }

  // Vérifie que toutes les catégories (hors "Généralités") sont complètes
  private areAllCategoriesComplete(): boolean {
    const f = this.fat();
    if (!f) return false;
    for (const cat of f.categories) {
      if (cat.name === 'Généralités') continue;
      if (this.isCategorySubcontractorMissing(cat)) return false;
      const allFields = cat.subcategories.flatMap((s: FatSubcategoryDto) => s.fields);
      const complete = allFields.every((field: FatFieldDto) => {
        const hasResult = field.result !== null && field.result !== undefined && field.result !== '';
        const hasRequiredComment = !this.isCommentMandatory(field) || (field.comment !== null && field.comment !== undefined && field.comment.trim() !== '');
        const hasRequiredAttachments = this.hasMinimumAttachments(field);
        return hasResult && hasRequiredComment && hasRequiredAttachments;
      });
      if (!complete) return false;
    }
    return true;
  }

  // Peut-on afficher le bouton signer actif
  canSignNow(): boolean {
    if (!this.canSign()) return false;
    return this.areAllCategoriesComplete();
  }

  getSignature(controllerNumber: number): FatSignatureDto | undefined {
    return this.fat()?.signatures?.find(s => s.controllerNumber === controllerNumber);
  }

  submitSignature(controllerNumber: number): void {
    if (!this.canSign()) {
      this.error.emit("Vous n'avez pas les droits pour signer la FAT.");
      return;
    }

    // Empêcher la signature si toutes les catégories ne sont pas complètes
    if (!this.areAllCategoriesComplete()) {
      const missing = this.getMissingCategories();
      this.validationErrorTitle.set('FAT incomplète');
      this.validationErrorMessage.set('Vous ne pouvez pas signer tant que toutes les catégories ne sont pas complètes. Veuillez compléter :');
      this.missingCategories.set(missing.length ? missing : ['Catégories incomplètes']);
      this.showValidationErrors.set(true);
      return;
    }

    const pad = controllerNumber === 1 ? this.signaturePad1 : this.signaturePad2;

    if (!pad || pad.isEmpty()) {
      this.error.emit('Veuillez dessiner votre signature.');
      return;
    }

    const data = pad.toDataURL('image/png');

    this.isLoading.set(true);
    this.fatService.signFat(this.projectId, this.fatId, controllerNumber, data).subscribe({
      next: (updatedFat) => {
        this.fat.set(updatedFat);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Erreur lors de la signature', err);
        const errorMessage = err.error?.message || 'Une erreur est survenue lors de la signature.';

        // Si l'erreur est liée à une règle de gestion des signatures (ex: double signature)
        // on l'affiche dans une modale
        if (errorMessage.includes('contrôleur') || errorMessage.includes('signer') || errorMessage.includes('catégories')) {
          this.validationErrorTitle.set('Règle de signature');
          this.validationErrorMessage.set('Impossible de valider la signature :');
          this.missingCategories.set([errorMessage]);
          this.showValidationErrors.set(true);
        } else {
          this.error.emit(errorMessage);
        }

        this.isLoading.set(false);
      }
    });
  }

  exportNonConformities(): void {
    this.isLoadingExport.set(true);
    const taskId = this.taskNotificationService.start('Export FAT', 'Génération du PDF en cours...');
    this.fatService.startExportJob(this.projectId, this.fatId).pipe(
      switchMap(job => this.fatService.waitForExportJob(this.projectId, job.jobId)),
      switchMap(job => this.fatService.downloadExportJob(this.projectId, job.jobId))
    ).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fat-${this.fat()?.fatNumber || this.fatId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.taskNotificationService.succeed(taskId, 'PDF généré.');
        this.isLoadingExport.set(false);
      },
      error: (err) => {
        console.error('Erreur lors de l\'export des non conformités', err);
        this.taskNotificationService.fail(taskId, err?.message || 'La génération du PDF a échoué.');
        this.error.emit(err?.message || 'Une erreur est survenue lors de la génération du fichier.');
        this.isLoadingExport.set(false);
      }
    });
  }
}
